// STP-77: Bundle splitter doesn't yet expose a DELETE/revoke endpoint, so when
// a user dismisses a stuck or failed task we record its task_id locally and
// hide it from the rehydration list on subsequent refreshes. The Celery task
// itself keeps running until it finishes naturally.

const STORAGE_KEY = "medirect.bundleSplitter.dismissedTaskIds";

// Tasks that age out of every list are never named for forgetting, so only
// the most recent dismissals are kept and older ones drop off.
const MAX_STORED_IDS = 200;

const readSet = (): Set<string> => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((v) => typeof v === "string") : []
    );
  } catch {
    return new Set();
  }
};

const writeSet = (set: Set<string>): void => {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify([...set].slice(-MAX_STORED_IDS))
    );
  } catch {
    // Quota or disabled storage — silently ignore; worst case the entry returns next refresh.
  }
};

export const getDismissedBundleTaskIds = (): Set<string> => readSet();

export const addDismissedBundleTaskId = (taskId: string): void => {
  if (!taskId) return;
  const set = readSet();
  if (set.has(taskId)) return;
  set.add(taskId);
  writeSet(set);
};

// Forget the given dismissed ids — callers pass tasks their case's list
// reports as ended, which are never recovered again and can't hold a file name
// (MDRS-550). Ids not named are kept: pruning to "ids this case's list still
// reports" used to wipe every other case's dismissals.
export const forgetDismissedBundleTaskIds = (taskIds: Iterable<string>): void => {
  const current = readSet();
  if (current.size === 0) return;
  const forget = new Set(taskIds);
  const next = new Set([...current].filter((id) => !forget.has(id)));
  if (next.size === current.size) return;
  writeSet(next);
};
